import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CriteriaRowWrapper from '../CriteriaRowWrapper';
import { useTranslation } from 'Components/Helpers/translate';

type Filter = {
	value?: string;
	comparisonType: string;
	inactive?: boolean;
};

type Props = {
	filter: Filter;
	config: { filterName: string; placeholder?: string };
	onChange: (value: string, filter: Filter) => void;
};

const FreeTextInput: React.FC<Props> = ({ filter, config, onChange }) => {
	const { t } = useTranslation();
	const [draftValue, setDraftValue] = useState(() => filter.value ?? '');

	useEffect(() => {
		const next = filter.value ?? '';
		setDraftValue(prev => (prev === next ? prev : next));
	}, [filter.value]);

	const placeholder = useMemo(() => {
		if (config.placeholder) {
			return config.placeholder;
		}
		if (config.filterName.includes('Custom')) {
			return t('default.freeText');
		}
		const key = `segment.placeholder.${config.filterName}`;
		const translated = t(key);
		return translated === key ? t('segment.selectCriteriaPlaceholder') : translated;
	}, [config.filterName, config.placeholder]);

	const commitValue = useCallback(
		(nextValue: string) => {
			if (nextValue === (filter.value ?? '')) {
				return;
			}
			const trimmed = nextValue.trim();
			const nextFilter = {
				...filter,
				value: nextValue,
				inactive: trimmed.length === 0
			};
			onChange(nextValue, nextFilter);
		},
		[filter, onChange]
	);

	const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
		setDraftValue(event.target.value);
	};

	const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
		commitValue(draftValue);
	};

	const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
		}
	};

	return (
		<CriteriaRowWrapper className="criteria-row" max800w={false} rowFlex={false}>
			<div className={draftValue.trim() ? 'multi-comparitors-wrap active' : 'multi-comparitors-wrap'}>
				<input
					type="text"
					className="form-control"
					value={draftValue}
					onChange={handleChange}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
				/>
			</div>
		</CriteriaRowWrapper>
	);
};

export default FreeTextInput;
